import React, { useState, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import cardData from '../data/Donation';
import { motion } from 'framer-motion';

const Helps = () => {
  const [inView, setInView] = useState(false);
  const sectionRef = useRef(null);

  // IntersectionObserver to trigger animations when the section comes into view
  useEffect(() => {
    const sectionElement = sectionRef.current; // Store the ref value in a variable
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          setInView(true);
        } else {
          setInView(false);
        }
      },
      { threshold: 0.2 } // Trigger when 20% of the section is visible
    );

    if (sectionElement) {
      observer.observe(sectionElement);
    }

    // Cleanup observer on component unmount
    return () => {
      if (sectionElement) {
        observer.unobserve(sectionElement);
      }
    };
  }, []); // Empty dependency array ensures the effect runs only once

  return (
    <section
      ref={sectionRef}
      className="relative py-12 w-full min-h-[40vh] bg-white"
    >
      <div className="mx-auto w-[90%] md:w-[70%] space-y-6">
        <motion.p
          className="text-sm text-[#90444D] font-semibold uppercase font-playfair tracking-wider"
          initial={{ opacity: 0 }}
          animate={{ opacity: inView ? 1 : 0 }}
          transition={{ duration: 1 }}
        >
          To Help
        </motion.p>
        <motion.h2
          className="text-2xl md:text-3xl font-bold font-playfair text-gray-900 space-y-6 tracking-wider"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: inView ? 1 : 0, y: inView ? 0 : 50 }}
          transition={{ duration: 1 }}
        >
          How Could You <span className="text-[#90444D]">Help Us?</span>
        </motion.h2>

        <div className="w-full px-4">
          <Swiper
            modules={[Navigation, Pagination]}
            loop={true}
            pagination={{ clickable: true }}
            spaceBetween={50}
            breakpoints={{
              0: { slidesPerView: 1 },
              768: { slidesPerView: 3 },
              1024: { slidesPerView: 3 },
            }}
          >
            {cardData.map((card, index) => (
              <SwiperSlide key={index}>
                <motion.div
                  className="group flex items-start gap-6 transition-shadow duration-300 h-full mb-12"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: inView ? 1 : 0, scale: inView ? 1 : 0.9 }}
                  transition={{ duration: 0.6, delay: index * 0.3 }}
                >
                  <div className="flex-shrink-0">
                    <img
                      src={card.image}
                      alt={card.alt}
                      className="w-16 h-16 object-contain"
                    />
                  </div>
                  <div>
                    <motion.h3
                      className="text-xl font-semibold text-[#90444D] mb-2 font-Cardo"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: inView ? 1 : 0 }}
                      transition={{ duration: 0.6, delay: 0.3 }}
                    >
                      {card.title}
                    </motion.h3>
                    <motion.p
                      className="leading-relaxed"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: inView ? 1 : 0 }}
                      transition={{ duration: 0.6, delay: 0.5 }}
                    >
                      {card.description}
                    </motion.p>
                  </div>
                </motion.div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </section>
  );
};

export default Helps;
